import { formatUnits, parseUnits } from "@ethersproject/units"
import { useGasPrice } from "@usedapp/core"
import type { BigNumber } from "ethers"
import { Contract } from "ethers"
import { useCallback, useEffect, useState } from "react"
import { BITSabi } from "web3/abi/bits"

import { bnNumberOrZero } from "utils/web3/bn-number-or-zero"

import { useErrorModal } from "./use-error-modal"
import { useTokenBalanceInner } from "./use-token-balance"
import type { WALLET_CONNECTION_TYPES } from "./use-wallet"
import { useWallet } from "./use-wallet"

// USE ONLY AS PARENT FOR ERC721 TOKEN HOOKS
export function useBBToken(contractAddress: string, connectionType?: WALLET_CONNECTION_TYPES) {
  const { account, readProvider, signer } = useWallet({ connectionType })
  const gasPrice = useGasPrice()
  const { handleTransaction } = useErrorModal()
  const [balance, setBalance] = useState<number | undefined>()
  const [balanceBn, setBalanceBn] = useState<BigNumber | undefined>()
  const balanceInnerBn = useTokenBalanceInner(contractAddress, account)

  useEffect(() => {
    setBalance(balanceInnerBn ? bnNumberOrZero(balanceInnerBn) : undefined)
    setBalanceBn(balanceInnerBn)
  }, [balanceInnerBn])

  const getAllowance = useCallback(
    async (spender: string) => {
      if (!readProvider) return

      const contract = new Contract(contractAddress, BITSabi, readProvider)
      const result = await contract.allowance(account, spender, {
        gasLimit: 1_500_000,
      })

      return Number(formatUnits(result, 18))
    },
    [readProvider, contractAddress, account],
  )

  const getAllowanceLimits = useCallback(
    async (spender: string, price = 100) => {
      const allowance = await getAllowance(spender)

      if (typeof allowance !== "undefined") {
        return {
          enough: Number(allowance) >= price,
          price,
          allowance,
        }
      }
    },
    [getAllowance],
  )

  const approve = useCallback(
    async (spender: string, limit = 1000000): Promise<boolean> => {
      if (!signer || !gasPrice) return false

      try {
        const contract = new Contract(contractAddress, BITSabi, signer)
        await handleTransaction(
          contract.approve(spender, parseUnits(limit.toString(), 18), {
            gasLimit: 1_500_000,
            gasPrice: gasPrice.toString(),
          }),
        )

        return true
      } catch (err) {
        console.log(err)
        return false
      }
    },
    [contractAddress, handleTransaction, signer, gasPrice],
  )

  const transfer = useCallback(
    async (recipientWalletAddress: string, amount = 0): Promise<boolean> => {
      if (!signer || !gasPrice) return false

      try {
        const contract = new Contract(contractAddress, BITSabi, signer)
        await handleTransaction(
          contract.transfer(recipientWalletAddress, parseUnits(amount.toString(), 18), {
            gasLimit: 1_500_000,
            gasPrice: gasPrice.toString(),
          }),
        )

        return true
      } catch (err) {
        console.log(err)
        return false
      }
    },
    [contractAddress, gasPrice, handleTransaction, signer],
  )

  return {
    account,
    balance,
    balanceBn,
    getAllowanceLimits,
    getAllowance,
    approve,
    transfer,
  }
}
